"use client";

import React, { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import {
  ChevronLeft,
  Shield,
  Star,
  Clock,
  Mail,
  Phone,
  Award,
  Leaf,
  Lock,
  Building2,
  Calendar,
  Package,
  MessageCircle,
  MapPin,
  Share,
  Instagram,
  Facebook,
} from "lucide-react";
import { useMerchantProfile } from "@hooks/useMerchantProfile";
import styles from "./page.module.scss";

function MerchantProfileContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [showContactModal, setShowContactModal] = useState(false);
  // const [showAllReviews, setShowAllReviews] = useState(false);
  const [merchant, setMerchant] = useState<any>(null);
  // const [animatedStats, setAnimatedStats] = useState({
  //   onTimeDelivery: 0,
  //   customerSatisfaction: 0,
  //   returnRate: 0,
  // });

  const merchantId = searchParams?.get("id");

  const { merchantProfile, loading, error } = useMerchantProfile(merchantId);

  useEffect(() => {
    console.log("merchantProfile", merchantProfile);
    if (!merchantProfile) return;

    const merchant = {
      name: merchantProfile.merchant_name?.replace(".myshopify.com", ""),
      domain: merchantProfile.shop_link,
      logo: merchantProfile.logo_url,
      coverImage: Array.isArray(merchantProfile.cover_image_urls)
        ? merchantProfile.cover_image_urls[0]
        : merchantProfile.cover_image_urls,
      gallery:
        Array.isArray(merchantProfile.cover_image_urls) &&
        merchantProfile.cover_image_urls.length > 0
          ? merchantProfile.cover_image_urls
          : merchantProfile.cover_image_urls
          ? [merchantProfile.cover_image_urls]
          : [],
      totalOrders: merchantProfile?.quick_info?.total_orders,
      categories: merchantProfile.categories?.map((cat) =>
        cat.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      ),
      established: merchantProfile.quick_info?.established_at
        ? new Date(merchantProfile.quick_info.established_at).getFullYear()
        : undefined,
      responseTime: merchantProfile.quick_info?.response_time,
      location: merchantProfile?.address,
      contactInformation: {
        supportEmail: merchantProfile?.contact_information?.support_email,
        supportPhone: merchantProfile?.contact_information?.support_phone,
        instagram: merchantProfile?.contact_information?.instagram,
        facebook: merchantProfile?.contact_information?.facebook,
      },
      description: merchantProfile.business_description,
      policies: {
        returns: merchantProfile?.policies?.return_policy,
        shipping: merchantProfile?.policies?.shipping,
        support: merchantProfile?.policies?.support,
        warranty: merchantProfile?.policies?.warranty,
      },
      topProducts: merchantProfile.top_products,
    };
    setMerchant(merchant);
  }, [merchantProfile]);

  // useEffect(() => {
  //   if (activeTab === "stats" && merchant) {
  //     const timer = setTimeout(() => {
  //       setAnimatedStats({
  //         onTimeDelivery: 0,
  //         customerSatisfaction: 0,
  //         returnRate: 0,
  //       });
  //     }, 100);
  //     return () => clearTimeout(timer);
  //   }
  // }, [activeTab, merchant]);

  if (merchantId && error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerContainer}>
            <div className={styles.headerInner}>
              <button
                className={styles.backButton}
                onClick={() => router.back()}
              >
                <ChevronLeft className={styles.backIcon} />
              </button>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            fontSize: "18px",
            color: "#666",
            textAlign: "center",
          }}
        >
          Error loading merchant profile: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContainer}>
          <div className={styles.headerInner}>
            <button className={styles.backButton} onClick={() => router.back()}>
              <ChevronLeft className={styles.backIcon} />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.contentGrid}>
          <div className={styles.leftColumn}>
            <div className={styles.galleryContainer}>
              <div className={styles.gallery}>
                <div className={styles.galleryMobileImage}>
                  <Image
                    src={merchant?.gallery[0] || "/default-store-image.jpg"}
                    alt="Main store image"
                    width={400}
                    height={300}
                    className={styles.galleryImage}
                    loading="lazy"
                  />
                </div>

                <div className={styles.galleryDesktop}>
                  <div className={styles.galleryMainImage}>
                    <Image
                      src={merchant?.gallery[0] || "/default-store-image.jpg"}
                      alt="Main store image"
                      width={600}
                      height={400}
                      className={styles.galleryImage}
                      priority
                    />
                  </div>

                  {merchant?.gallery?.length > 1 && (
                    <>
                      <div className={styles.galleryRightColumn}>
                        {merchant?.gallery?.length > 1 && (
                          <div className={styles.gallerySmallImage}>
                            <Image
                              src={
                                merchant?.gallery[1] ||
                                "/default-store-image.jpg"
                              }
                              alt="Store image"
                              width={300}
                              height={200}
                              className={styles.galleryImage}
                              loading="lazy"
                            />
                          </div>
                        )}
                        {merchant?.gallery?.length > 2 && (
                          <div className={styles.gallerySmallImage}>
                            <Image
                              src={
                                merchant?.gallery[2] ||
                                "/default-store-image.jpg"
                              }
                              alt="Store image"
                              width={300}
                              height={200}
                              className={styles.galleryImage}
                              loading="lazy"
                            />
                          </div>
                        )}
                      </div>

                      {merchant?.gallery?.length > 3 && (
                        <div className={styles.galleryRightColumn}>
                          {merchant?.gallery?.length > 3 && (
                            <div className={styles.gallerySmallImage}>
                              <Image
                                src={
                                  merchant?.gallery[3] ||
                                  "/default-store-image.jpg"
                                }
                                alt="Store image"
                                width={300}
                                height={200}
                                className={styles.galleryImage}
                                loading="lazy"
                              />
                            </div>
                          )}
                          {merchant?.gallery?.length > 4 && (
                            <div className={styles.gallerySmallImage}>
                              <Image
                                src={
                                  merchant?.gallery[4] ||
                                  "/default-store-image.jpg"
                                }
                                alt="Store image"
                                width={300}
                                height={200}
                                className={styles.galleryImage}
                                loading="lazy"
                              />
                            </div>
                          )}
                          {merchant?.gallery?.length > 5 && (
                            <div
                              className={`${styles.gallerySmallImage} ${styles.galleryOverlay}`}
                            >
                              <Image
                                src={
                                  merchant?.gallery[5] ||
                                  "/default-store-image.jpg"
                                }
                                alt="Store image"
                                width={300}
                                height={200}
                                className={styles.galleryImage}
                                loading="lazy"
                              />
                              <div className={styles.galleryOverlayInner}>
                                <span className={styles.galleryOverlayText}>
                                  +{merchant?.gallery?.length - 5} more
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.titleSection}>
              <div className={styles.titleContainer}>
                <Image
                  src={merchant?.logo || "/default-merchant-logo.png"}
                  alt={merchant?.name || "Merchant logo"}
                  width={80}
                  height={80}
                  className={styles.logo}
                  loading="lazy"
                />
                <div className={styles.titleInfo}>
                  <div className={styles.titleHeader}>
                    <h1 className={styles.title}>{merchant?.name}</h1>
                    {/* <button className={styles.shareButton}>
                      <Share className={styles.shareIcon} />
                    </button> */}
                  </div>
                  <div className={styles.ratingContainer}>
                    {merchant?.totalOrders > 0 && (
                      <div className={styles.ratingInner}>
                        <span className={styles.reviewCount}>
                          {merchant.totalOrders} orders
                        </span>
                      </div>
                    )}
                    {merchant?.totalOrders > 0 && merchant?.location && (
                      <span className={styles.separator}>•</span>
                    )}
                    {merchant?.location && (
                      <div className={styles.locationContainer}>
                        <MapPin className={styles.locationIcon} />
                        <span className={styles.locationText}>
                          {merchant.location}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className={styles.badgesContainer}>
                    <div className={styles.safeBuyBadge}>
                      <Shield className={styles.safeBuyIcon} />
                      <span className={styles.safeBuyText}>
                        SafeBuy Protected
                      </span>
                    </div>

                    {merchant?.categories?.map(
                      (category: string, i: number) => (
                        <span key={i} className={styles.categoryBadge}>
                          {category}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.tabContainer}>
              <div className={styles.tabNavigation}>
                <nav className={styles.tabList}>
                  {["overview", "stats", "Contact & Support"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`${styles.tabButton} ${
                        activeTab === tab ? styles.active : ""
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
            <div className={styles.tabContent}>
              {activeTab === "overview" && (
                <div className={styles.tabSection}>
                  {merchant?.description && (
                    <div>
                      <h3 className={styles.sectionTitle}>
                        About {merchant?.name}
                      </h3>
                      <p className={styles.descriptionText}>
                        {merchant?.description}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* {activeTab === "products" && (
                <div>
                  <h3 className={styles.sectionTitle}>Featured Products</h3>
                  <div className={styles.productsGrid}>
                    {merchant?.topProducts &&
                    Array.isArray(merchant?.topProducts) ? (
                      merchant?.topProducts.map((product: any) => (
                        <div key={product.id} className={styles.productCard}>
                          <div className={styles.productImageContainer}>
                            <Image
                              src={
                                product.image || "/default-product-image.jpg"
                              }
                              alt={product.name}
                              width={250}
                              height={250}
                              className={styles.productImage}
                              loading="lazy"
                            />
                            {product.discount > 0 && (
                              <div className={styles.discountBadge}>
                                -{product.discount}%
                              </div>
                            )}
                            <div className={styles.productOverlay}>
                              <button className={styles.productOverlayButton}>
                                Quick View
                              </button>
                            </div>
                          </div>
                          <h4 className={styles.productName}>{product.name}</h4>
                          <div className={styles.productPricing}>
                            <span className={styles.productPrice}>
                              ₹
                              {product.discount > 0
                                ? Math.round(
                                    product.price * (1 - product.discount / 100)
                                  )
                                : product.price}
                            </span>
                            {product.discount > 0 && (
                              <span className={styles.productOriginalPrice}>
                                ₹{product.price}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={styles.noDataMessage}>
                        <p>No products available at the moment.</p>
                      </div>
                    )}
                  </div>
                  <div className={styles.centerContent}>
                    <button className={styles.viewAllButton}>
                      View All Products
                    </button>
                  </div>
                </div>
              )} */}

              {/* {activeTab === "reviews" && (
                <div>
                  <div className={styles.reviewsSummary}>
                    <div className={styles.noDataMessage}>
                      <p>
                        Rating and review data is not available for this
                        merchant.
                      </p>
                    </div>
                  </div>
                  <div className={styles.reviewsList}>
                    <div className={styles.noDataMessage}>
                      <p>Customer reviews are not available at the moment.</p>
                    </div>
                  </div>
                </div>
              )} */}

              {/* {showAllReviews && (
                <div className={styles.modalOverlay}>
                  <div className={`${styles.modal} ${styles.large}`}>
                    <div className={styles.modalHeader}>
                      <div className={styles.modalHeaderContent}>
                        <div>
                          <h3 className={styles.modalTitle}>All Reviews</h3>
                          <div className={styles.ratingContainer}>
                            <div className={styles.noDataMessage}>
                              <p>Rating data not available</p>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowAllReviews(false)}
                          className={styles.modalCloseButton}
                        >
                          <svg
                            className={styles.modalCloseIcon}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className={`${styles.modalBody} ${styles.scrollable}`}>
                      <div className={styles.reviewsList}>
                        <div className={styles.noDataMessage}>
                          <p>
                            No reviews are available for this merchant at the
                            moment.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )} */}

              {activeTab === "stats" && (
                <div className={styles.tabSection}>
                  {/* <div>
                    <h3 className={styles.sectionTitle}>Performance Metrics</h3>
                    <div className={styles.statsGrid}>
                      <div className={`${styles.statCard} ${styles.green}`}>
                        <div className={`${styles.statNumber} ${styles.green}`}>
                          <span style={{ display: "inline-block" }}>
                            {animatedStats.onTimeDelivery.toFixed(1)}%
                          </span>
                        </div>
                        <div className={`${styles.statLabel} ${styles.green}`}>
                          On-time Delivery
                        </div>
                      </div>
                      <div className={`${styles.statCard} ${styles.blue}`}>
                        <div className={`${styles.statNumber} ${styles.blue}`}>
                          <span style={{ display: "inline-block" }}>
                            {animatedStats.customerSatisfaction.toFixed(1)}%
                          </span>
                        </div>
                        <div className={`${styles.statLabel} ${styles.blue}`}>
                          Customer Satisfaction
                        </div>
                      </div>
                      <div className={`${styles.statCard} ${styles.purple}`}>
                        <div
                          className={`${styles.statNumber} ${styles.purple}`}
                        >
                          <span style={{ display: "inline-block" }}>
                            {animatedStats.returnRate.toFixed(1)}%
                          </span>
                        </div>
                        <div className={`${styles.statLabel} ${styles.purple}`}>
                          Return Rate
                        </div>
                      </div>
                      <div className={`${styles.statCard} ${styles.orange}`}>
                        <div
                          className={`${styles.statNumber} ${styles.orange}`}
                        >
                          --
                        </div>
                        <div className={`${styles.statLabel} ${styles.orange}`}>
                          Avg. Delivery
                        </div>
                      </div>
                    </div>
                  </div> */}
                  <div>
                    <h3 className={styles.sectionTitle}>Trust Indicators</h3>
                    <div className={styles.trustGrid}>
                      <div className={`${styles.trustCard} ${styles.green}`}>
                        <div className={styles.trustHeader}>
                          <Shield
                            className={`${styles.trustIcon} ${styles.green}`}
                          />
                          <div className={styles.trustTitle}>
                            SafeBuy Protection
                          </div>
                        </div>
                        <div className={styles.trustDescription}>
                          Verified merchant with full protection
                        </div>
                      </div>
                      {merchant?.responseTime && merchant?.responseTime > 0 && (
                        <div className={`${styles.trustCard} ${styles.blue}`}>
                          <div className={styles.trustHeader}>
                            <Clock
                              className={`${styles.trustIcon} ${styles.blue}`}
                            />
                            <div className={styles.trustTitle}>
                              Response Time
                            </div>
                          </div>
                          <div className={styles.trustDescription}>
                            &lt; {merchant.responseTime} hours average response
                          </div>
                        </div>
                      )}
                      {new Date().getFullYear() - merchant.established > 0 && (
                        <div className={`${styles.trustCard} ${styles.purple}`}>
                          <div className={styles.trustHeader}>
                            <Building2
                              className={`${styles.trustIcon} ${styles.purple}`}
                            />
                            <div className={styles.trustTitle}>
                              Business Since
                            </div>
                          </div>

                          <div className={styles.trustDescription}>
                            {merchant.established} •{" "}
                            {new Date().getFullYear() - merchant.established}+
                            years experience
                          </div>
                        </div>
                      )}
                      {merchant?.totalOrders > 0 && (
                        <div className={`${styles.trustCard} ${styles.indigo}`}>
                          <div className={styles.trustHeader}>
                            <Package
                              className={`${styles.trustIcon} ${styles.indigo}`}
                            />
                            <div className={styles.trustTitle}>
                              Total Orders
                            </div>
                          </div>
                          <div className={styles.trustDescription}>
                            {merchant.totalOrders}+ successful orders
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Contact & Support" && (
                <div className={styles.tabSection}>
                  {/* <div>
                    <h3 className={styles.sectionTitle}>Leadership Team</h3>
                    <div className={styles.teamList}>
                      <div className={styles.noDataMessage}>
                        <p>Team information is not available at the moment.</p>
                      </div>
                    </div>
                  </div> */}
                  <div>
                    <h3 className={styles.sectionTitle}>Contact & Support</h3>
                    <div className={styles.contactGrid}>
                      <button
                        className={`${styles.contactButton} ${styles.email}`}
                      >
                        <Mail
                          className={`${styles.contactIcon} ${styles.email}`}
                        />
                        <div className={styles.contactInfo}>
                          <div className={styles.contactTitle}>
                            {merchant?.contactInformation?.supportEmail}{" "}
                          </div>
                          <div className={styles.contactValue}>{} </div>
                        </div>
                      </button>

                      <button
                        className={`${styles.contactButton} ${styles.phone}`}
                      >
                        <Phone
                          className={`${styles.contactIcon} ${styles.phone}`}
                        />
                        <div className={styles.contactInfo}>
                          <div className={styles.contactTitle}></div>
                          <div className={styles.contactValue}>
                            {merchant?.contactInformation?.supportPhone}{" "}
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                  <div>
                    <h3 className={styles.sectionTitle}>Follow Us</h3>
                    <div className={styles.socialGrid}>
                      {merchant?.contactInformation?.instagram && (
                        <button
                          className={`${styles.socialButton} ${styles.instagram}`}
                          onClick={() => {
                            window.open(
                              merchant.contactInformation.instagram,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                        >
                          <Image
                            src="https://acko-cms.ackoassets.com/image_5_138a326258.svg"
                            alt="Instagram"
                            width={32}
                            height={32}
                            className={styles.socialIcon}
                            loading="lazy"
                          />
                        </button>
                      )}

                      {merchant?.contactInformation?.facebook && (
                        <button
                          className={`${styles.socialButton} ${styles.facebook}`}
                          onClick={() => {
                            window.open(
                              merchant?.contactInformation?.facebook,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                        >
                          <Image
                            src="https://acko-cms.ackoassets.com/image_390_104080cd00.svg"
                            alt="Facebook"
                            width={32}
                            height={32}
                            className={styles.socialIcon}
                            loading="lazy"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.sidebar}>
            <div className={styles.sidebarSticky}>
              <div className={styles.ctaCard}>
                <div className={styles.ctaHeader}>
                  <h3 className={styles.ctaTitle}>Visit {merchant?.name}</h3>
                  <p className={styles.ctaDescription}>
                    Browse products with SafeBuy protection
                  </p>
                </div>

                <button
                  className={styles.ctaPrimaryButton}
                  onClick={() => {
                    if (merchant?.domain) {
                      const url = merchant.domain.startsWith("http")
                        ? merchant.domain
                        : `https://${merchant.domain}`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  Shop Now
                </button>

                <button
                  onClick={() => setShowContactModal(true)}
                  className={styles.ctaSecondaryButton}
                >
                  <MessageCircle className={styles.messageIcon} />
                  Contact Merchant
                </button>
              </div>
              <div className={styles.quickInfoCard}>
                <h4 className={styles.quickInfoTitle}>Quick Info</h4>
                <div className={styles.quickInfoList}>
                  {merchant?.responseTime && merchant?.responseTime > 0 && (
                    <div className={styles.quickInfoItem}>
                      <div className={styles.quickInfoLabel}>
                        <Clock className={styles.quickInfoIcon} />
                        <span className={styles.quickInfoText}>
                          Response time
                        </span>
                      </div>
                      <span className={styles.quickInfoValue}>
                        &lt; {merchant?.responseTime} hours
                      </span>
                    </div>
                  )}
                  {merchant?.established && (
                    <>
                      <div className={styles.quickInfoItem}>
                        <div className={styles.quickInfoLabel}>
                          <Calendar className={styles.quickInfoIcon} />
                          <span className={styles.quickInfoText}>
                            Established
                          </span>
                        </div>
                        <span className={styles.quickInfoValue}>
                          {merchant?.established}
                        </span>
                      </div>
                    </>
                  )}
                  {merchant?.totalOrders > 0 && (
                    <div className={styles.quickInfoItem}>
                      <div className={styles.quickInfoLabel}>
                        <Package className={styles.quickInfoIcon} />
                        <span className={styles.quickInfoText}>
                          Total orders
                        </span>
                      </div>
                      <span className={styles.quickInfoValue}>
                        {merchant?.totalOrders}+
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showContactModal && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modal} ${styles.medium}`}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderContent}>
                <div className={styles.modalHeaderLeft}>
                  <Image
                    src={merchant?.logo}
                    alt={merchant?.name}
                    width={48}
                    height={48}
                    className={styles.modalLogo}
                    loading="lazy"
                  />
                  <div className={styles.modalHeaderInfo}>
                    <h3 className={styles.modalTitle}>{merchant?.name}</h3>
                    <p className={styles.modalSubtitle}>
                      Usually responds in {merchant?.responseTime} hours
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowContactModal(false)}
                  className={styles.modalCloseButton}
                >
                  <svg
                    className={styles.modalCloseIcon}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSection}>
                <div>
                  <h4 className={styles.modalSectionTitle}>
                    Contact Information
                  </h4>
                  <div className={styles.modalSection}>
                    <div className={styles.modalContactItem}>
                      <Mail className={styles.modalContactIcon} />
                      <div className={styles.modalContactInfo}>
                        <div className={styles.modalContactTitle}>
                          Email Support
                        </div>
                        <div className={styles.modalContactValue}>
                          {merchant?.contactInformation?.supportEmail}
                        </div>
                      </div>
                    </div>
                    <div className={styles.modalContactItem}>
                      <Phone className={styles.modalContactIcon} />
                      <div className={styles.modalContactInfo}>
                        <div className={styles.modalContactTitle}>
                          Phone Support
                        </div>
                        <div className={styles.modalContactValue}>
                          {merchant?.contactInformation?.supportPhone}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* <div>
                  <h4 className={styles.modalSectionTitle}>Business Hours</h4>
                  <div className={styles.businessHours}>
                    <div className={styles.businessHoursGrid}>
                      <div>
                        <div className={styles.businessHoursLabel}>
                          Weekdays
                        </div>
                        <div className={styles.businessHoursValue}>
                          9:00 AM - 6:00 PM
                        </div>
                      </div>
                      <div>
                        <div className={styles.businessHoursLabel}>
                          Weekends
                        </div>
                        <div className={styles.businessHoursValue}>
                          10:00 AM - 4:00 PM
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.modalFooter}>
                  All times are in local timezone (IST)
                </div> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main component with Suspense wrapper
export default function MerchantProfile() {
  return (
    <Suspense
      fallback={
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading merchant profile...</p>
          </div>
        </div>
      }
    >
      <MerchantProfileContent />
    </Suspense>
  );
}
